import { useState } from 'react'
import { Copy, Plus, Users2Icon, X } from 'lucide-react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { useCancelInvite, useCreateInvite, useGetUserConnections, useUserPendingInvitations } from '../api'
import { getInviteUrl } from '../utils/helpers'

import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip } from '@/components/ui/tooltip'
import { paths } from '@/config/path'
import { ROLES } from '@/lib/authorization'
import { cn } from '@/lib/utils'
import { logger } from '@/logger'
import { useModalControls } from '@/state/modals'
import { useAuthenticatedUser } from '@/state/session'

export function ConnectionsDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const user = useAuthenticatedUser()
  const userProfile = user.profile.profile
  const navigate = useNavigate()
  const { openModal } = useModalControls()
  const { data: connectionsData } = useGetUserConnections({
    queryConfig: {
      enabled: isOpen,
    },
  })
  const connections = connectionsData || []
  const { data } = useUserPendingInvitations({
    queryConfig: {
      enabled: isOpen,
    },
  })
  const pendingInvites = data || []

  const createInviteMutation = useCreateInvite({
    mutationConfig: {
      onSuccess: ({ url, expiresAt }) => {
        openModal({
          name: 'share-invite-code',
          url,
          title: `Invitation (expire le ${new Date(expiresAt).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })})`,
        })
      },
      onError: (error) => {
        logger.error('Failed to create invite', { errorMessage: error })
        toast.error("Une erreur est survenue lors de la création de l'invitation")
      },
    },
  })

  const cancelInviteMutation = useCancelInvite()

  const formatTimeRemaining = (expiresAtStr: string) => {
    const expiresAt = new Date(expiresAtStr)
    const now = new Date()
    const diff = expiresAt.getTime() - now.getTime()

    if (diff <= 0) return 'Expiré'

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

    if (days > 0) return `${days}j ${hours}h`
    return `${hours}h`
  }

  const isExpired = (expiresAt: string) => {
    return new Date() > new Date(expiresAt)
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Tooltip content={userProfile === ROLES.PRACTICIAN ? 'Mes Techniciens' : 'Mes Praticiens'} delayDuration={1000}>
          <Button variant="ghost" size={'icon'}>
            <Users2Icon />
          </Button>
        </Tooltip>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 p-0 border-slate-200" align="end">
        <div className="bg-gradient-to-b from-white to-slate-50/30 rounded-lg border border-slate-200">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100/50">
            <h3 className="text-slate-900 font-medium text-sm">
              {userProfile === ROLES.PRACTICIAN ? 'Collaborer avec vos Techniciens' : 'Collaborer avec vos Praticiens'}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {userProfile === ROLES.PRACTICIAN
                ? 'Gérez vos connexions avec les techniciens dentaires'
                : 'Gérez vos connexions avec les praticiens dentaires'}
            </p>
          </div>

          {/* Connected Users Section */}
          <div className="px-4 pb-4 pt-4">
            <h4 className="text-xs text-slate-500 mb-3 font-normal">
              {userProfile === ROLES.PRACTICIAN ? 'Techniciens connectés' : 'Praticiens connectés'}
            </h4>

            {connections.length === 0 ? (
              <div className="py-3 px-2 bg-slate-50/50 rounded-lg border border-slate-100">
                <p className="text-xs text-slate-600 mb-2 font-medium">
                  {userProfile === ROLES.PRACTICIAN ? 'Aucun technicien connecté' : 'Aucun praticien connecté'}
                </p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {userProfile === ROLES.PRACTICIAN
                    ? 'Invitez vos techniciens habituels pour simplifier vos commandes et collaborations.'
                    : 'Connectez-vous avec vos praticiens pour recevoir leurs commandes directement.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {connections.map((connection) => (
                  <div key={connection.connectionId} className="flex items-center gap-2 group py-1">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs text-emerald-700 font-medium">
                        {getInitials(connection.connectedUserName)}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700 font-normal text-xs truncate">{connection.connectedUserName}</p>
                    </div>

                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* {userProfile === ROLES.PRACTICIAN && connection.connectionId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 h-auto"
                          onClick={() => {
                            navigate(paths.technician.profile.getHref(connection.connectedUserName))
                            setIsOpen(false)
                          }}
                          title="Voir le profil"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      )} */}

                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-slate-400 hover:text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 h-auto font-normal"
                        onClick={() => {
                          if (userProfile === ROLES.PRACTICIAN) {
                            navigate(
                              paths.app['search-service'].getHref({
                                technicianDisplayName: connection.connectedUserName,
                              }),
                            )
                          } else if (userProfile === ROLES.TECHNICIAN) {
                            navigate(paths.app.orders.getHref(connection.connectedUserName))
                          }
                          setIsOpen(false)
                        }}
                        title={userProfile === ROLES.PRACTICIAN ? 'Passer une commande' : 'Voir les commandes'}
                      >
                        {userProfile === ROLES.PRACTICIAN ? 'Commander' : 'Commandes'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Invites Section */}
          <div className="border-t border-slate-100 px-4 py-4">
            <h4 className="text-xs text-slate-500 mb-3 font-normal">Invitations en attente</h4>

            {pendingInvites.length === 0 ? (
              <div className="py-2">
                <p className="text-xs text-slate-400">Aucune invitation en cours</p>
                <p className="text-xs text-slate-500 mt-1">Créez un lien pour inviter un nouveau collaborateur</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingInvites.map((invite) => {
                  const expired = isExpired(invite.expiresAt)
                  return (
                    <div key={invite.id} className={cn('flex items-center gap-2 group py-1', expired && 'opacity-40')}>
                      <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-amber-700 font-medium">
                          {formatDate(invite.createdAt).split('/')[0]}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={cn('text-xs font-normal truncate', expired ? 'text-red-500' : 'text-slate-700')}>
                          {expired ? 'Expiré' : `${formatTimeRemaining(invite.expiresAt)} restant`}
                        </p>
                      </div>

                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 h-auto"
                          disabled={expired}
                          onClick={() => {
                            const link = getInviteUrl(invite.inviteCode)
                            navigator.clipboard.writeText(link)
                            toast('Lien copié dans le presse-papiers')
                          }}
                          title="Copier le lien d'invitation"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-red-500 hover:bg-red-50 px-1.5 py-0.5 h-auto"
                          onClick={() => cancelInviteMutation.mutate(invite.inviteCode)}
                          isLoading={cancelInviteMutation.isPending}
                          disabled={cancelInviteMutation.isPending || expired}
                          title="Annuler l'invitation"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Generate New Invite Button */}
          <div className="border-t border-slate-100 px-4 py-3">
            <Button
              variant="ghost"
              className="w-full gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 py-2 h-auto font-normal text-sm"
              disabled={createInviteMutation.isPending}
              isLoading={createInviteMutation.isPending}
              onClick={() => {
                createInviteMutation.mutate({
                  forProfile: userProfile === ROLES.TECHNICIAN ? ROLES.PRACTICIAN : ROLES.TECHNICIAN,
                })
                setIsOpen(false)
              }}
            >
              <Plus className="h-4 w-4" />
              {userProfile === ROLES.PRACTICIAN ? 'Inviter un technicien' : 'Inviter un praticien'}
            </Button>
            <p className="text-xs text-slate-500 mt-2 text-center">
              {userProfile === ROLES.PRACTICIAN
                ? 'Partagez le lien avec votre technicien habituel'
                : 'Partagez le lien avec un praticien pour recevoir ses commandes'}
            </p>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
